/**
 * useList - Generic list hook for any repository
 * 
 * Replaces specialized list hooks (useProperties, useMailshots, etc.)
 * with a single generic implementation. Eliminates ~900 lines of duplication!
 * 
 * Usage:
 *   const { entities, loading, error, refresh, pagination, ... } = useList({
 *     repository: propertyRepository,
 *     options: { where: [['status', '==', 'active']], orderBy: 'createdAt' }
 *   });
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Generic list hook for any repository with real-time updates
 * @param {Object} config - Configuration object
 * @param {Object} config.repository - Repository instance (PropertyRepository, MailshotRepository, etc.)
 * @param {Object} config.options - Query options (where, orderBy, limit, etc.)
 * @param {boolean} config.realtime - Enable real-time updates (default: true)
 * @param {boolean} config.immediate - Fetch immediately on mount (default: true)
 * @returns {Object} - { entities, loading, error, refresh, isEmpty, count, ... }
 */
export function useList({
  repository,
  options = {},
  realtime = true,
  immediate = true
}) {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);
  
  const unsubscribeRef = useRef(null);
  const mountedRef = useRef(true);

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  // Serialize options so an inline object literal with identical contents doesn't
  // re-create fetchEntities and tear down / re-subscribe the Firestore listener on
  // every render. Callers that already memoize their options are unaffected.
  const optionsKey = JSON.stringify(options ?? {});

  /**
   * Fetch entities from repository
   */
  const fetchEntities = useCallback(async () => {
    if (!repository) {
      console.warn('🔧 [useList] No repository provided');
      return;
    }

    // Mark as mounted when setting up subscription (handles remounting)
    mountedRef.current = true;

    try {
      setLoading(true);
      setError(null);

      if (realtime) {
        // Real-time updates via subscription

        // Clean up previous subscription
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
        }

        // Subscribe to real-time updates
        const unsubscribe = repository.subscribeAll(options, (updatedEntities) => {
          if (!mountedRef.current) {
            return;
          }

          setEntities(updatedEntities);
          setCount(updatedEntities.length);
          setLoading(false);
        }, (err) => {
          if (!mountedRef.current) return;
          
          console.error('🔧 [useList] Subscription error:', err);
          setError(err.message || 'Failed to subscribe to updates');
          setLoading(false);
        });

        unsubscribeRef.current = unsubscribe;

      } else {
        // One-time fetch
        const fetchedEntities = await repository.findAll(options);
        
        if (!mountedRef.current) return;
        
        setEntities(fetchedEntities);
        setCount(fetchedEntities.length);
        setLoading(false);
      }

    } catch (err) {
      if (!mountedRef.current) return;
      
      console.error('🔧 [useList] Error fetching entities:', err);
      setError(err.message || 'Failed to fetch entities');
      setLoading(false);
    }
    // optionsKey is the serialized form of options — see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repository, optionsKey, realtime]);

  /**
   * Refresh entities (for one-time fetches or manual refresh)
   */
  const refresh = useCallback(() => {
    fetchEntities();
  }, [fetchEntities]);

  /**
   * Initial fetch on mount (if immediate=true)
   */
  useEffect(() => {
    if (immediate) {
      fetchEntities();
    }
  }, [immediate, fetchEntities]);

  return {
    entities,
    loading,
    error,
    refresh,
    count,
    isEmpty: !loading && entities.length === 0,
    hasError: !!error
  };
}

/**
 * useListWithPagination - Generic list hook with pagination support
 * 
 * Usage:
 *   const { entities, loading, page, totalPages, nextPage, prevPage, ... } = useListWithPagination({
 *     repository: propertyRepository,
 *     options: { where: [['status', '==', 'active']] },
 *     pageSize: 20
 *   });
 */
export function useListWithPagination({
  repository,
  options = {},
  pageSize = 20,
  realtime = true
}) {
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Build options with pagination
  const paginatedOptions = {
    ...options,
    limit: pageSize,
    offset: (page - 1) * pageSize
  };

  const {
    entities,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError
  } = useList({
    repository,
    options: paginatedOptions,
    realtime,
    immediate: true
  });

  // Fetch total count (for accurate pagination)
  useEffect(() => {
    if (!repository) return;

    const fetchTotalCount = async () => {
      try {
        const allEntities = await repository.findAll(options);
        setTotalCount(allEntities.length);
      } catch (err) {
        console.error('🔧 [useListWithPagination] Error fetching total count:', err);
      }
    };

    fetchTotalCount();
  }, [repository, options]);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  }, [page]);

  const goToPage = useCallback((pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  }, [totalPages]);

  return {
    entities,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError,
    // Pagination
    page,
    pageSize,
    totalCount,
    totalPages,
    nextPage,
    prevPage,
    goToPage,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1
  };
}

/**
 * useListWithFilters - Generic list hook with client-side filtering
 * 
 * Usage:
 *   const { entities, filteredEntities, setFilters, clearFilters, ... } = useListWithFilters({
 *     repository: propertyRepository,
 *     filterFn: (entity, filters) => entity.status === filters.status
 *   });
 */
export function useListWithFilters({
  repository,
  options = {},
  filterFn = null,
  realtime = true
}) {
  const [filters, setFilters] = useState({});
  
  const {
    entities,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError
  } = useList({
    repository,
    options,
    realtime,
    immediate: true
  });

  // Apply client-side filters
  const filteredEntities = filterFn 
    ? entities.filter(entity => filterFn(entity, filters))
    : entities;

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  return {
    entities: filteredEntities,
    allEntities: entities,
    loading,
    error,
    refresh,
    count: filteredEntities.length,
    totalCount: count,
    isEmpty: !loading && filteredEntities.length === 0,
    hasError,
    // Filters
    filters,
    setFilters,
    clearFilters,
    isFiltered: Object.keys(filters).length > 0
  };
}
